import { useEffect, useState, type FormEvent } from 'react';
import {
  editableTaskTypes,
  lifecycleEndLabels,
  lifecycleEndTransitions,
  taskPriorities,
  taskPriorityLabels,
  taskTypeLabels,
  type EditableTaskType,
  type LifecycleEndTransition,
  type TaskNextStep,
  type TaskPriority,
} from '@brimo/shared';
import {
  Banner,
  Button,
  Checkbox,
  Dialog,
  FieldGroup,
  FormRow,
  RadioCard,
  Select,
  TextField,
  Textarea,
} from '../../ds/components';
import { useWorkspace } from '../../lib/session';
import { useTasks } from './api';
import { addDays, isValidDayKey, isValidTime, todayKey } from './dates';
import styles from './NextStepDialog.module.css';

/** The follow-up step of „Nächster Schritt fehlt" (docs/dev/api-tasks.md `NextStep`). */
export type NextStep = TaskNextStep;

export interface NextStepDialogProps {
  open: boolean;
  contactName: string;
  /** „Zurück zur Task" — the only way out besides submitting. */
  onBack: () => void;
  onSubmit: (nextStep: NextStep) => void | Promise<void>;
  pending?: boolean | undefined;
  /** Server message of a failed resubmission, shown as a banner. */
  error?: string | null | undefined;
  /** Top edge of the scrim: 104 keeps the queue bar visible in focus mode (Queue 07), default 60. */
  overlayTop?: number | undefined;
  /** Extra class of the dialog panel (the focus mode places its dialogs 48 px under the bar). */
  className?: string | undefined;
}

type Kind = NextStep['kind'];

const KIND_OPTIONS: ReadonlyArray<{ kind: Kind; title: string; description?: string }> = [
  { kind: 'follow_up', title: 'Wiedervorlage', description: 'Datum und Zuständiger' },
  { kind: 'book_appointment', title: 'Termin buchen', description: 'Link oder direkt' },
  { kind: 'task', title: 'Aufgabe anlegen' },
  {
    kind: 'lifecycle_end',
    title: 'Lifecycle beenden',
    description: 'Kein Interesse, Disqualifiziert oder DNC',
  },
];

const DATE_REQUIRED = 'Bitte ein Datum wählen.';
const TIME_INVALID = 'Bitte eine Uhrzeit im Format HH:MM angeben.';
const TITLE_REQUIRED = 'Bitte einen Titel angeben.';
const REASON_REQUIRED = 'Bitte geben Sie einen Grund an.';

interface Errors {
  dueOn?: string;
  dueTime?: string;
  title?: string;
  reason?: string;
}

/**
 * Queue 07 „Nächster Schritt fehlt": the mandatory follow-up after completing a task that would leave the
 * contact without an open step. Radio tiles Wiedervorlage / Termin buchen / Aufgabe anlegen / Lifecycle
 * beenden with the fields of the chosen step; not dismissable except through „Zurück zur Task".
 */
export function NextStepDialog({
  open,
  contactName,
  onBack,
  onSubmit,
  pending = false,
  error,
  overlayTop,
  className,
}: NextStepDialogProps) {
  const workspace = useWorkspace();
  const tasks = useTasks(false);
  const today = todayKey();
  const [kind, setKind] = useState<Kind>('follow_up');
  const [dueOn, setDueOn] = useState(() => addDays(today, 1));
  const [dueTime, setDueTime] = useState('');
  const [assigneeId, setAssigneeId] = useState(workspace.user.id);
  const [note, setNote] = useState('');
  const [title, setTitle] = useState('');
  const [taskType, setTaskType] = useState<EditableTaskType>('call');
  const [priority, setPriority] = useState<TaskPriority>('b');
  const [transition, setTransition] = useState<LifecycleEndTransition>('no_interest');
  const [reason, setReason] = useState('');
  const [gdpr, setGdpr] = useState(false);
  const [errors, setErrors] = useState<Errors>({});

  useEffect(() => {
    if (!open) return;
    setKind('follow_up');
    setDueOn(addDays(todayKey(), 1));
    setDueTime('');
    setAssigneeId(workspace.user.id);
    setNote('');
    setTitle('');
    setTaskType('call');
    setPriority('b');
    setTransition('no_interest');
    setReason('');
    setGdpr(false);
    setErrors({});
  }, [open, workspace.user.id]);

  const assignees = tasks.data?.assignees;
  const me = `${workspace.user.firstName.charAt(0)}. ${workspace.user.lastName}`.trim();
  const assigneeOptions =
    assignees && assignees.length > 1
      ? assignees.map((user) => ({ value: user.id, label: user.name }))
      : null;

  function validateDate(): Errors {
    const next: Errors = {};
    if (!isValidDayKey(dueOn)) next.dueOn = DATE_REQUIRED;
    if (dueTime !== '' && !isValidTime(dueTime)) next.dueTime = TIME_INVALID;
    return next;
  }

  function buildStep(): NextStep | null {
    let next: Errors = {};
    let step: NextStep | null = null;
    switch (kind) {
      case 'follow_up': {
        next = validateDate();
        step = {
          kind: 'follow_up',
          dueOn,
          dueTime: dueTime === '' ? null : dueTime,
          assigneeId,
          note: note.trim() === '' ? null : note.trim(),
        };
        break;
      }
      case 'task': {
        next = validateDate();
        if (title.trim() === '') next.title = TITLE_REQUIRED;
        step = {
          kind: 'task',
          title: title.trim(),
          dueOn,
          dueTime: dueTime === '' ? null : dueTime,
          assigneeId,
          type: taskType,
          priority,
        };
        break;
      }
      case 'book_appointment':
        step = { kind: 'book_appointment' };
        break;
      case 'lifecycle_end': {
        if (reason.trim() === '') next.reason = REASON_REQUIRED;
        step = {
          kind: 'lifecycle_end',
          transition,
          reason: reason.trim(),
          ...(transition === 'dnc' ? { requestGdprDeletion: gdpr } : {}),
        };
        break;
      }
    }
    setErrors(next);
    return Object.keys(next).length === 0 ? step : null;
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    if (pending) return;
    const step = buildStep();
    if (step !== null) void onSubmit(step);
  }

  const assigneeField = assigneeOptions ? (
    <Select
      label="Zuständig"
      options={assigneeOptions}
      value={assigneeId}
      onChange={(event) => setAssigneeId(event.target.value)}
    />
  ) : (
    <TextField label="Zuständig" value={me} readOnly />
  );

  return (
    <Dialog
      open={open}
      onClose={onBack}
      width={600}
      eyebrow="Nächster Schritt fehlt"
      title={`Wie geht es mit ${contactName} weiter?`}
      subtitle="Sie haben die Task erledigt, aber es gibt danach nichts mehr zu tun. DAMOKLES lässt keinen Kontakt ohne Folgeschritt zurück."
      closeOnScrim={false}
      initialFocus="none"
      overlayTop={overlayTop}
      className={className}
      footerNote="DNC braucht keinen Folgeschritt"
      footer={
        <>
          <Button variant="secondary" onClick={onBack} disabled={pending}>
            Zurück zur Task
          </Button>
          <Button type="submit" form="next-step-form" loading={pending}>
            Speichern und weiter
          </Button>
        </>
      }
    >
      <form id="next-step-form" className={styles.form} onSubmit={submit} noValidate>
        {error ? <Banner tone="danger">{error}</Banner> : null}
        <FieldGroup columns={2} aria-label="Nächster Schritt">
          {KIND_OPTIONS.map((option) => (
            <RadioCard
              key={option.kind}
              name="next-step-kind"
              value={option.kind}
              title={option.title}
              description={option.description}
              checked={kind === option.kind}
              onChange={() => {
                setKind(option.kind);
                setErrors({});
              }}
            />
          ))}
        </FieldGroup>

        {kind === 'follow_up' ? (
          <>
            <FormRow columns={3}>
              <TextField
                label="Wiedervorlage am"
                type="date"
                required
                autoFocus
                value={dueOn}
                error={errors.dueOn}
                min={today}
                onChange={(event) => setDueOn(event.target.value)}
              />
              <TextField
                label="Uhrzeit"
                type="time"
                value={dueTime}
                error={errors.dueTime}
                onChange={(event) => setDueTime(event.target.value)}
              />
              {assigneeField}
            </FormRow>
            <TextField
              label="Notiz"
              optional
              value={note}
              onChange={(event) => setNote(event.target.value)}
            />
          </>
        ) : null}

        {kind === 'task' ? (
          <>
            <TextField
              label="Titel"
              required
              autoFocus
              value={title}
              error={errors.title}
              onChange={(event) => setTitle(event.target.value)}
            />
            <FormRow columns={2}>
              <Select
                label="Art"
                value={taskType}
                options={editableTaskTypes.map((type) => ({ value: type, label: taskTypeLabels[type] }))}
                onChange={(event) => setTaskType(event.target.value as EditableTaskType)}
              />
              <Select
                label="Priorität"
                value={priority}
                options={taskPriorities.map((value) => ({ value, label: taskPriorityLabels[value] }))}
                onChange={(event) => setPriority(event.target.value as TaskPriority)}
              />
            </FormRow>
            <FormRow columns={3}>
              <TextField
                label="Fällig am"
                type="date"
                required
                value={dueOn}
                error={errors.dueOn}
                min={today}
                onChange={(event) => setDueOn(event.target.value)}
              />
              <TextField
                label="Uhrzeit"
                type="time"
                value={dueTime}
                error={errors.dueTime}
                onChange={(event) => setDueTime(event.target.value)}
              />
              {assigneeField}
            </FormRow>
          </>
        ) : null}

        {kind === 'book_appointment' ? (
          <Banner tone="neutral">
            DAMOKLES legt die Zwischenaufgabe „Termin buchen“ an und öffnet nach dem Speichern die
            Terminbuchung in der Kontaktakte.
          </Banner>
        ) : null}

        {kind === 'lifecycle_end' ? (
          <>
            <FieldGroup legend="Neuer Status" columns={3}>
              {lifecycleEndTransitions.map((value) => (
                <RadioCard
                  key={value}
                  name="lifecycle-end"
                  value={value}
                  title={lifecycleEndLabels[value]}
                  checked={transition === value}
                  onChange={() => setTransition(value)}
                />
              ))}
            </FieldGroup>
            <Textarea
              label="Grund"
              required
              rows={3}
              value={reason}
              error={errors.reason}
              onChange={(event) => setReason(event.target.value)}
            />
            {transition === 'dnc' ? (
              <Checkbox
                label="Zusätzlich DSGVO-Löschung beantragen"
                checked={gdpr}
                onChange={(event) => setGdpr(event.target.checked)}
              />
            ) : null}
          </>
        ) : null}
      </form>
    </Dialog>
  );
}
